import React, { type JSX } from 'react'
import styled from '@emotion/styled'
import { SkyTvBadge } from '@mobi/component-library/Areas/Racing/SkyTvIndicator'
import { Icon, type IconType } from '@mobi/component-library/Legacy/Icon'
import { FeatureFlags } from '@mobi/settings'
import { TimeToJumpComponent } from '@core/Areas/NextEvents/Components/TimeToJump/TimeToJump'
import { buildToteRaceUri, getNextRaceTimeDisplayOptions } from '@core/Areas/Racing/helpers'
import { isThoroughbredNextRaceInWA } from '@core/Areas/Racing/helpers/liveVideoFeedHelpers'
import { getVisionChannelForDisplay } from '@core/Areas/SkyVideoPlayer/helpers'
import { NextRace as NextRaceData } from '@core/Data/Racing/nextRaces'
import { fontFamilies } from '@core/Settings'
import { useFeature } from '@core/Utils/hooks'
import { HomeNextRacesListItemStyled } from './HomeNextRaces.styles'

interface IconTypes {
  Races: IconType
  Trots: IconType
  Dogs: IconType
}

const IconTypes: IconTypes = {
  Races: 'races',
  Trots: 'trot',
  Dogs: 'dog',
}

export const HomeNextRacesTile = ({ race }: { race: NextRaceData }): JSX.Element => {
  const [shouldShowDay, setShouldShowDay] = React.useState<boolean>(
    () => getNextRaceTimeDisplayOptions(race.AdvertisedStartTime).showDay
  )

  const [shouldShowCountdown, setShouldShowCountdown] = React.useState<boolean>(
    () => getNextRaceTimeDisplayOptions(race.AdvertisedStartTime).showCountDown
  )

  const isRacingWAFeatureActive = useFeature(FeatureFlags.RACING_WA_VISION_CHANNEL.key)

  React.useEffect(() => {
    const interval = window.setInterval(() => {
      const { showCountDown, showDay } = getNextRaceTimeDisplayOptions(race.AdvertisedStartTime)
      setShouldShowCountdown(current => (showCountDown !== current ? showCountDown : current))
      setShouldShowDay(current => (showDay !== current ? showDay : current))
    }, 1_000)

    return () => {
      window.clearInterval(interval)
    }
  }, [race.AdvertisedStartTime])

  const href = buildToteRaceUri({
    meetingId: race.MeetingID,
    raceNumber: race.RaceNumber,
    meetingDate: race.MeetingDate,
  })

  const startTime = new Date(race.AdvertisedStartTime)

  const shouldDisplayRacingWA = isRacingWAFeatureActive && isThoroughbredNextRaceInWA(race)

  return (
    <HomeNextRacesListItemStyled data-tid-home-next-events='home-next-events-item' to={href}>
      <HomeNextRacesListContainer>
        <HomeNextEventNameEventStyled>
          <div>{race.MeetingName}</div>
          <div>R{race.RaceNumber}</div>
        </HomeNextEventNameEventStyled>

        {race.SkyTvChannelId != null && (
          <HomeNextEventRaceSkyStyled>
            <SkyTvBadge
              channel={getVisionChannelForDisplay(race.SkyTvChannelId, shouldDisplayRacingWA)}
            />
          </HomeNextEventRaceSkyStyled>
        )}

        <HomeNextEventRaceTimeStyled>
          <Icon size='2rem' type={IconTypes[race.RaceType as keyof IconTypes]} />
          <TimeToJumpComponent
            size='1.2rem'
            advertisedStartTime={startTime}
            showCountDown={shouldShowCountdown}
            showDay={shouldShowDay}
          />
        </HomeNextEventRaceTimeStyled>
      </HomeNextRacesListContainer>
    </HomeNextRacesListItemStyled>
  )
}

// ======
// Styles
// ======

const HomeNextRacesListContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
  maxHeight: '100%',
})

const HomeNextEventNameEventStyled = styled.div({
  fontSize: '1.1rem',
  fontFamily: fontFamilies.brand,
  lineHeight: 1.1,

  div: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
})

const HomeNextEventRaceTimeStyled = styled.div({
  fontSize: '1.2rem',
  justifyContent: 'space-between',
  display: 'flex',

  '& > span': {
    display: 'flex',
    alignItems: 'center',

    'div:last-of-type': {
      paddingLeft: '0.2rem',
    },
  },
})

const HomeNextEventRaceSkyStyled = styled.div({
  display: 'flex',
  justifyContent: 'right',
  marginTop: '-0.75rem',
  marginBottom: '-0.5rem',
})
